/**
 * Y GO Shuttle Services France - Pages Légales
 * Mentions légales, CGV, Confidentialité, Cookies
 * Branché au CMS : fetch `/api/cms/pages/legal-{type}/public` puis fallback local
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { Header, Footer } from '../components/layout';
import { useCMSPage, getCMSSection } from '../hooks/useCMSPage';
import config from 'config';

const API_URL = config.API_URL;

// Mapping type param → CMS page slug
const CMS_SLUG_MAP = {
  mentions: 'legal-mentions',
  cgv: 'legal-cgv',
  confidentialite: 'legal-confidentialite',
  // cookies: no CMS page — static only
};

// Helper: format phone for display
const fmtPhone = (raw) => {
  if (!raw) return '';
  if (raw.includes(' ')) return raw;
  return raw.replace(/^\+(\d{2})(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/, '+$1 $2 $3 $4 $5 $6');
};

// Build dynamic mentions légales content
const buildMentionsContent = (c) => {
  const name = c.legal_name || c.company_name || 'Y GO SHUTTLE SERVICES FRANCE';
  const form = c.legal_form ? `Société par actions simplifiée (${c.legal_form})` : 'Société par actions simplifiée (SAS)';
  const capital = c.share_capital || '10 000 €';
  const addr = c.address || '51 quai Southampton';
  const cp = c.postal_code || '76600';
  const city = c.city || 'Le Havre';
  const country = c.country || 'France';
  const siret = c.siret || '';
  const rcs = c.rcs_city ? `${c.rcs_city} B ${c.siren || ''}` : '';
  const tva = c.tva_number || '';
  const phone = fmtPhone(c.phone) || '+33 2 78 78 04 04';
  const email = c.email || 'booking@y-go.fr';

  return `
## Éditeur du site

**${name}**
${form}
Capital social : ${capital}

**Siège social :**
${addr}
${cp} ${city}, ${country}

**SIRET :** ${siret}${rcs ? `\n**RCS :** ${rcs}` : ''}${tva ? `\n**TVA intracommunautaire :** ${tva}` : ''}

**Directeur de la publication :**
M. [Nom du dirigeant]

---

## Hébergement

**OVHcloud**
2 rue Kellermann, 59100 Roubaix, France
Site web : www.ovhcloud.com

---

## Activité réglementée

${name} exerce l'activité de Voiture de Transport avec Chauffeur (VTC) conformément à la réglementation française :
- Loi n° 2014-1104 du 1er octobre 2014
- Décret n° 2014-1725 du 30 décembre 2014

**Numéro d'inscription au registre VTC :** EVTC076XXXXXX

---

## Propriété intellectuelle

L'ensemble du contenu du site (textes, images, logos, éléments graphiques) est protégé par le droit d'auteur et le droit des marques.

La marque "Y GO" et le logo associé sont des marques déposées.

Toute reproduction, représentation ou diffusion, totale ou partielle, du contenu de ce site est interdite sans autorisation écrite préalable.

---

## Crédits

**Conception et développement :**
${c.company_name || 'Y GO Shuttle Services France'}

**Photographies :**
Unsplash, Pexels (libres de droits)

---

## Contact

Pour toute question relative aux mentions légales :
- Email : ${email}
- Téléphone : ${phone}
- Courrier : ${name}, ${addr}, ${cp} ${city}
    `;
};

// Build dynamic CGV content
const buildCgvContent = (c) => {
  const phone = fmtPhone(c.phone) || '+33 2 78 78 04 04';
  const email = c.email || 'booking@y-go.fr';

  return `
## Article 1 – Objet

Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre ${c.legal_name || 'Y GO SHUTTLE SERVICES FRANCE'} (ci-après "Y GO") et ses clients (ci-après "le Client") pour toute prestation de transport avec chauffeur.

---

## Article 2 – Réservation

### 2.1 Modalités de réservation
Les réservations peuvent être effectuées :
- En ligne sur le site ${c.website || 'y-go.fr'}
- Par téléphone au ${phone}
- Par email à ${email}

### 2.2 Confirmation
Toute réservation est confirmée par email après acceptation du devis et réception du paiement (total ou acompte selon conditions).

### 2.3 Informations requises
Le Client s'engage à fournir des informations exactes : coordonnées, adresses de prise en charge et destination, nombre de passagers, bagages, vol ou train (pour les transferts aéroport/gare).

---

## Article 3 – Tarifs et paiement

### 3.1 Tarification
Les tarifs sont exprimés en euros TTC. Ils comprennent :
- Le transport du point de départ au point d'arrivée
- Les péages autoroutiers
- Le parking (dans la limite de 15 minutes d'attente)

### 3.2 Suppléments
Des majorations peuvent s'appliquer :
- Course de nuit (22h-6h) : +20%
- Weekend (samedi-dimanche) : +15%
- Jour férié : +25%
- Attente supplémentaire : 0,50€/minute

### 3.3 Moyens de paiement
Paiement accepté par :
- Carte bancaire (Visa, Mastercard, American Express)
- Virement bancaire (entreprises)

---

## Article 4 – Annulation et modification

### 4.1 Par le Client
- Annulation plus de 48h avant : remboursement intégral
- Annulation entre 24h et 48h : 50% du montant retenu
- Annulation moins de 24h : aucun remboursement

### 4.2 Par Y GO
En cas d'impossibilité d'exécuter la prestation (force majeure, incident technique), Y GO s'engage à :
- Proposer un véhicule de remplacement
- Ou rembourser intégralement le Client

---

## Article 5 – Exécution de la prestation

### 5.1 Ponctualité
Y GO s'engage à respecter les horaires convenus. Un délai de grâce de 15 minutes est accordé au Client.

### 5.2 Attente
Pour les transferts aéroport/gare, le chauffeur attend :
- 30 minutes après l'heure d'atterrissage prévue (vols)
- 15 minutes après l'heure d'arrivée prévue (trains)

### 5.3 Non-présentation
En cas de non-présentation du Client sans annulation préalable, la prestation est réputée effectuée et reste due.

---

## Article 6 – Responsabilité

### 6.1 Assurance
Y GO dispose d'une assurance responsabilité civile professionnelle couvrant les passagers et leurs bagages.

### 6.2 Bagages
Y GO n'est pas responsable des bagages laissés dans le véhicule après la course. Le Client doit vérifier ses effets personnels.

### 6.3 Retards
Y GO n'est pas responsable des retards dus à des circonstances indépendantes de sa volonté (embouteillages, conditions météorologiques, accidents).

---

## Article 7 – Données personnelles

Le traitement des données personnelles est conforme au RGPD. Voir notre Politique de confidentialité pour plus de détails.

---

## Article 8 – Réclamations

Toute réclamation doit être adressée par écrit dans un délai de 7 jours suivant la prestation à : ${email}

---

## Article 9 – Droit applicable

Les présentes CGV sont soumises au droit français. Tout litige sera porté devant les tribunaux compétents du ressort ${c.rcs_city ? `de ${c.rcs_city}` : 'du Havre'}.

---

*Dernière mise à jour : Janvier 2026*
    `;
};

// Build dynamic confidentialité content
const buildConfidentialiteContent = (c) => {
  const name = c.legal_name || 'Y GO SHUTTLE SERVICES FRANCE';
  const addr = c.address || '51 quai Southampton';
  const cp = c.postal_code || '76600';
  const city = c.city || 'Le Havre';
  const country = c.country || 'France';

  return `
## Introduction

${name} (ci-après "Y GO") s'engage à protéger la vie privée de ses utilisateurs. Cette politique décrit comment nous collectons, utilisons et protégeons vos données personnelles.

---

## Responsable du traitement

**${name}**
${addr}
${cp} ${city}, ${country}
Email : privacy@y-go.fr

---

## Données collectées

### Données d'identification
- Nom, prénom
- Adresse email
- Numéro de téléphone

### Données de réservation
- Adresses de prise en charge et destination
- Date et heure de transport
- Nombre de passagers
- Informations de vol/train

### Données de paiement
Les données de paiement sont traitées de manière sécurisée par notre prestataire Stripe. Y GO ne stocke pas vos numéros de carte bancaire.

### Données de navigation
- Adresse IP
- Cookies (voir notre politique cookies)
- Pages consultées

### Données de géolocalisation des chauffeurs
- Position GPS en temps réel (latitude, longitude)
- Précision du signal GPS
- Cap (direction de déplacement) et vitesse
- Horodatage de chaque point GPS enregistré

---

## Géolocalisation des chauffeurs — Conditions d'utilisation

### Pourquoi collectons-nous la position GPS des chauffeurs ?

La collecte de la position géographique en temps réel des chauffeurs partenaires et salariés de Y GO répond à des finalités précises et légitimes :

**1. Exécution du service de transport**
La géolocalisation permet d'assurer le bon déroulement des missions de transport :
- Affectation optimale des missions aux chauffeurs les plus proches du lieu de prise en charge
- Suivi en temps réel de la progression de la mission pour le client et le dispatching
- Calcul de l'heure d'arrivée estimée (ETA) communiquée au client
- Vérification du respect de l'itinéraire et des points d'arrêt prévus

**2. Sécurité des passagers et des chauffeurs**
- Localisation du véhicule en cas d'incident ou d'urgence
- Traçabilité du trajet effectué pour la résolution de litiges éventuels

**3. Qualité de service et conformité**
- Évaluation de la ponctualité et du respect des horaires
- Optimisation des plannings et de la répartition des missions
- Conformité avec les obligations réglementaires applicables aux exploitants VTC

### Quand le GPS est-il actif ?

**Le suivi GPS est strictement limité aux périodes d'activité professionnelle du chauffeur :**

- **Mission en cours** : La position du chauffeur est collectée en temps réel dès qu'une mission lui est assignée et qu'il se signale en approche, et ce jusqu'à la fin de mission (dépose du client et clôture de la mission dans l'application). Le client peut consulter la progression du véhicule via un lien de suivi sécurisé.

- **Statut "En ligne"** : Lorsque le chauffeur se met volontairement en ligne via l'application pour signaler sa disponibilité, sa position peut être transmise au système de dispatching afin de permettre l'affectation de nouvelles missions. La fréquence de collecte est réduite par rapport à une mission active.

### Quand le GPS est-il inactif ?

**Y GO ne collecte aucune donnée de géolocalisation dans les situations suivantes :**

- **Statut "Hors ligne"** : Lorsque le chauffeur se déconnecte de l'application ou bascule en mode hors ligne, le suivi GPS cesse immédiatement. Aucune position n'est enregistrée ni transmise.

- **Application fermée** : Si l'application mobile est fermée ou si le chauffeur n'est pas authentifié, aucune collecte de position n'est effectuée.

- **En dehors des missions** : Lorsqu'aucune mission n'est en cours et que le chauffeur ne s'est pas signalé comme disponible, aucun suivi GPS n'est actif.

- **Temps de pause et repos** : Les périodes de pause déclarées par le chauffeur ne font l'objet d'aucun suivi de position.

**Le chauffeur garde à tout moment le contrôle sur l'activation du GPS** en basculant son statut entre "En ligne" et "Hors ligne" dans l'application. Ce choix est libre et sans conséquence disciplinaire en dehors des plages de mission planifiées.

### Qui a accès aux données de géolocalisation ?

- **Les administrateurs et dispatchers de Y GO** : pour la gestion opérationnelle et l'affectation des missions
- **Le client** : accès limité à la position du chauffeur assigné à sa mission, uniquement pendant la durée de la course, via un lien de suivi temporaire et sécurisé qui expire à la fin de la mission
- **Aucun tiers** : les données de géolocalisation ne sont ni vendues ni partagées avec des tiers à des fins commerciales

### Durée de conservation des données GPS

- **Données de position en temps réel** : conservées pendant la durée de la mission puis archivées
- **Historique des trajets (missions)** : 90 jours pour les besoins opérationnels et la résolution de litiges
- **Données agrégées et anonymisées** : peuvent être conservées sans limitation de durée à des fins statistiques

### Base légale du traitement

Le traitement des données de géolocalisation repose sur :
- **L'exécution du contrat de travail ou du contrat de partenariat** (article 6.1.b du RGPD) : le suivi GPS est nécessaire à la bonne exécution des missions de transport
- **L'intérêt légitime** (article 6.1.f du RGPD) : sécurité des passagers et optimisation du service
- **Le consentement éclairé** (article 6.1.a du RGPD) : le chauffeur est informé et consent au suivi lors de son inscription et à chaque mise en ligne

### Droits spécifiques des chauffeurs

En complément des droits généraux décrits ci-dessous, les chauffeurs disposent des droits suivants concernant leurs données de géolocalisation :
- **Droit d'accès** : obtenir l'intégralité de l'historique de leurs positions enregistrées
- **Droit de rectification** : signaler toute donnée de trajet incorrecte
- **Droit d'opposition** : refuser le suivi en dehors des missions actives en se mettant hors ligne
- **Droit à l'effacement** : demander la suppression de l'historique de position au-delà des obligations légales de conservation

Pour exercer ces droits : privacy@y-go.fr

---

## Finalités du traitement

Vos données sont utilisées pour :
1. **Exécuter nos services** : réservation, transport, facturation
2. **Communiquer avec vous** : confirmations, rappels, service client
3. **Améliorer nos services** : statistiques, qualité de service
4. **Géolocalisation des chauffeurs** : dispatch, suivi en temps réel, sécurité (voir section dédiée ci-dessus)
5. **Obligations légales** : comptabilité, fiscalité

---

## Base légale

- **Exécution du contrat** : traitement des réservations, géolocalisation pendant les missions
- **Consentement** : communications marketing, mise en ligne volontaire des chauffeurs
- **Intérêt légitime** : amélioration des services, sécurité des passagers
- **Obligation légale** : conservation comptable

---

## Durée de conservation

- Données de réservation : 5 ans (obligation comptable)
- Données de compte client : jusqu'à suppression du compte + 3 ans
- Données de géolocalisation : 90 jours (voir section dédiée)
- Cookies : voir politique cookies

---

## Vos droits

Conformément au RGPD, vous disposez des droits suivants :
- **Accès** : obtenir une copie de vos données
- **Rectification** : corriger vos données inexactes
- **Effacement** : supprimer vos données (sous conditions)
- **Limitation** : restreindre le traitement
- **Portabilité** : recevoir vos données dans un format standard
- **Opposition** : refuser certains traitements

Pour exercer vos droits : privacy@y-go.fr

---

## Suppression de compte et de vos données (Droit à l'effacement — Art. 17 RGPD)

Vous pouvez demander la suppression de votre compte et de l'ensemble de vos données personnelles à tout moment. Pour cela :

1. Rendez-vous sur notre page de demande de suppression : [Supprimer mon compte](/delete-account)
2. Entrez l'adresse email associée à votre compte
3. Un email de confirmation vous sera envoyé
4. Après confirmation, notre équipe traitera votre demande dans les plus brefs délais

**Données supprimées :** votre profil, vos informations personnelles, vos messages de contact et l'historique de vos communications.

**Données conservées :** conformément aux obligations comptables (Art. L123-22 du Code de Commerce), les données de facturation sont anonymisées et conservées pendant 5 ans.

Vous pouvez également envoyer votre demande par email à privacy@y-go.fr.

---

## Destinataires

Vos données peuvent être transmises à :
- Nos chauffeurs (données nécessaires à la prestation)
- Stripe (paiement sécurisé)
- Mailgun (envoi d'emails transactionnels)
- Google Maps (calcul d'itinéraires et affichage des positions)

---

## Sécurité

Nous mettons en œuvre des mesures techniques et organisationnelles appropriées :
- Chiffrement des données en transit (HTTPS/WSS)
- Chiffrement des communications WebSocket pour le suivi en temps réel
- Accès restreint aux données personnelles et de géolocalisation
- Liens de suivi public sécurisés par tokens uniques à durée limitée
- Hébergement sécurisé en Europe

---

## Réclamation

Vous pouvez introduire une réclamation auprès de la CNIL :
Commission Nationale de l'Informatique et des Libertés
3 Place de Fontenoy, TSA 80715
75334 PARIS CEDEX 07
www.cnil.fr

---

*Dernière mise à jour : Mars 2026*
    `;
};

// Static pages (cookies doesn't need company data)
const STATIC_PAGES = {
  cookies: {
    title: 'Politique de cookies',
    metaTitle: 'Cookies | Y GO Shuttle Services France',
    metaDescription: "Politique d'utilisation des cookies de Y GO Shuttle Services France.",
    content: `
## Qu'est-ce qu'un cookie ?

Un cookie est un petit fichier texte déposé sur votre appareil (ordinateur, smartphone, tablette) lors de votre visite sur notre site. Il permet de mémoriser vos préférences et d'améliorer votre expérience de navigation.

---

## Cookies utilisés

### Cookies essentiels
**Obligatoires** - Nécessaires au fonctionnement du site

| Cookie | Finalité | Durée |
|--------|----------|-------|
| access_token | Authentification utilisateur | Session |
| session_id | Identification de session | Session |

### Cookies de performance
**Optionnels** - Analyse et amélioration du site

| Cookie | Finalité | Durée |
|--------|----------|-------|
| _ga | Google Analytics - Statistiques | 2 ans |
| _gid | Google Analytics - Identification | 24h |

### Cookies fonctionnels
**Optionnels** - Personnalisation de l'expérience

| Cookie | Finalité | Durée |
|--------|----------|-------|
| preferred_language | Langue préférée | 1 an |
| cookie_consent | Préférences cookies | 1 an |

---

## Gestion des cookies

### Lors de votre première visite
Un bandeau vous permet d'accepter ou de refuser les cookies non essentiels.

### À tout moment
Vous pouvez modifier vos préférences :
- Via les paramètres de votre navigateur
- En nous contactant : privacy@y-go.fr

### Paramètres navigateur
- **Chrome** : Paramètres > Confidentialité > Cookies
- **Firefox** : Options > Vie privée > Cookies
- **Safari** : Préférences > Confidentialité > Cookies
- **Edge** : Paramètres > Cookies et autorisations

---

## Impact du refus

Le refus des cookies essentiels peut empêcher l'utilisation de certaines fonctionnalités (réservation en ligne, espace client).

Le refus des cookies optionnels n'affecte pas la navigation mais peut limiter la personnalisation.

---

## Cookies tiers

Certains services tiers peuvent déposer des cookies :
- **Google Maps** : affichage des cartes
- **Stripe** : sécurisation des paiements

Ces cookies sont soumis aux politiques de confidentialité de ces tiers.

---

## Durée de conservation

Les cookies ne sont pas conservés au-delà de 13 mois conformément aux recommandations de la CNIL.

---

## Plus d'informations

Pour en savoir plus sur les cookies : www.cnil.fr/cookies

---

*Dernière mise à jour : Janvier 2026*
    `
  }
};

// Dynamic page metadata
const PAGE_META = {
  mentions: {
    title: 'Mentions légales',
    metaTitle: 'Mentions légales | Y GO Shuttle Services France',
    metaDescription: 'Mentions légales de Y GO Shuttle Services France, société de VTC basée au Havre.',
  },
  cgv: {
    title: 'Conditions Générales de Vente',
    metaTitle: 'CGV | Y GO Shuttle Services France',
    metaDescription: 'Conditions générales de vente de Y GO Shuttle Services France, service VTC premium au Havre.',
  },
  confidentialite: {
    title: 'Politique de confidentialité',
    metaTitle: 'Politique de confidentialité | Y GO Shuttle Services France',
    metaDescription: 'Politique de confidentialité et protection des données personnelles de Y GO Shuttle Services France.',
  },
  cookies: {
    title: STATIC_PAGES.cookies.title,
    metaTitle: STATIC_PAGES.cookies.metaTitle,
    metaDescription: STATIC_PAGES.cookies.metaDescription,
  }
};

// Legal Page Component
export default function LegalPages() {
  const { type } = useParams();
  const [companyInfo, setCompanyInfo] = useState(null);
  const pageMeta = PAGE_META[type];

  // Fetch CMS content for legal pages (mentions, cgv, confidentialite)
  const cmsSlug = CMS_SLUG_MAP[type] || null;
  const { content: cmsContent, loading: cmsLoading } = useCMSPage(cmsSlug);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/public/company`);
        if (res.ok) {
          const data = await res.json();
          if (data && Object.keys(data).length > 0) setCompanyInfo(data);
        }
      } catch (err) {
        console.warn('Failed to fetch company info:', err);
      }
    };
    fetchCompany();
  }, []);
  
  useEffect(() => {
    if (pageMeta) {
      document.title = pageMeta.metaTitle;
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = pageMeta.metaDescription;
    }
    window.scrollTo(0, 0);
    return () => { document.title = 'Y GO Shuttle Services'; };
  }, [pageMeta]);

  // Resolve content: CMS first, then local fallback
  const c = companyInfo || {};
  const getContent = () => {
    // 1. Try CMS content (legal_text section)
    if (cmsSlug && cmsContent) {
      const legalSection = getCMSSection(cmsContent, 'legal_text');
      if (legalSection && legalSection.content) {
        return legalSection.content;
      }
    }
    // 2. Fallback to locally-built content
    if (type === 'cookies') return STATIC_PAGES.cookies.content;
    if (type === 'mentions') return buildMentionsContent(c);
    if (type === 'cgv') return buildCgvContent(c);
    if (type === 'confidentialite') return buildConfidentialiteContent(c);
    return null;
  };

  // Resolve page title from CMS banner or local meta
  const resolvedTitle = (cmsContent?.banner?.title) || pageMeta?.title;

  const content = getContent();
  
  if (!pageMeta) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <main className="pt-32 pb-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#E5E5E5] mb-4">
              Page non trouvée
            </h1>
            <Link to="/" className="text-[#C9A24D] hover:text-[#E6C77A]">
              Retour à l'accueil
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show loader while CMS is loading (only for CMS pages, not cookies)
  if (cmsSlug && cmsLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Header />
        <main className="pt-32 pb-20 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }
  
  // Simple markdown-like rendering
  const renderContent = (content) => {
    return content.split('\n').map((line, index) => {
      // Headers
      if (line.startsWith('## ')) {
        return (
          <h2 key={index} className="font-['Playfair_Display'] text-xl font-semibold text-[#E5E5E5] mt-8 mb-4 pb-2 border-b border-[#C9A24D]/20">
            {line.replace('## ', '')}
          </h2>
        );
      }
      if (line.startsWith('### ')) {
        return (
          <h3 key={index} className="font-semibold text-[#E5E5E5] mt-6 mb-2">
            {line.replace('### ', '')}
          </h3>
        );
      }
      
      // Horizontal rule
      if (line.trim() === '---') {
        return <hr key={index} className="border-[#C9A24D]/10 my-6" />;
      }
      
      // Empty line
      if (line.trim() === '') {
        return <br key={index} />;
      }
      
      // Bold text and links
      let processed = line;
      processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#E5E5E5]">$1</strong>');
      processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#C9A24D] underline hover:text-[#E6C77A]">$1</a>');
      
      // Numbered list items
      if (line.trim().match(/^\d+\.\s/)) {
        const num = line.trim().match(/^(\d+)\./)[1];
        const content = processed.replace(/^\d+\.\s/, '');
        return (
          <p key={index} className="text-[#E5E5E5]/70 text-sm ml-4 mb-1 flex gap-2">
            <span className="text-[#C9A24D] font-medium shrink-0">{num}.</span>
            <span dangerouslySetInnerHTML={{ __html: content }} />
          </p>
        );
      }
      
      // List items
      if (line.trim().startsWith('- ')) {
        return (
          <li key={index} className="text-[#E5E5E5]/70 text-sm ml-4 mb-1 list-disc" dangerouslySetInnerHTML={{ __html: processed.replace('- ', '') }} />
        );
      }
      
      // Table rows (simple)
      if (line.trim().startsWith('|')) {
        const cells = line.split('|').filter(c => c.trim());
        if (cells.every(c => c.trim().match(/^-+$/))) {
          return null; // Skip separator row
        }
        return (
          <tr key={index} className="border-b border-[#C9A24D]/10">
            {cells.map((cell, i) => (
              <td key={i} className="py-2 px-3 text-[#E5E5E5]/70 text-sm">
                {cell.trim()}
              </td>
            ))}
          </tr>
        );
      }
      
      // Regular paragraph
      return (
        <p key={index} className="text-[#E5E5E5]/70 text-sm leading-relaxed mb-2" dangerouslySetInnerHTML={{ __html: processed }} />
      );
    });
  };
  
  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8">
              <Link to="/" className="text-[#E5E5E5]/60 hover:text-[#C9A24D] transition-colors">
                Accueil
              </Link>
              <ChevronRight className="w-4 h-4 text-[#E5E5E5]/40" />
              <span className="text-[#C9A24D]">{resolvedTitle}</span>
            </nav>
            
            {/* Title */}
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#E5E5E5] mb-8">
              {resolvedTitle}
            </h1>
            
            {/* Content */}
            <div className="bg-[#1F1F1F] p-6 sm:p-8 lg:p-10 border border-[#C9A24D]/20">
              <div className="prose prose-invert max-w-none">
                {renderContent(content)}
              </div>
            </div>
            
            {/* Navigation links */}
            <div className="mt-8 pt-8 border-t border-[#C9A24D]/20">
              <h3 className="text-[#E5E5E5]/60 text-sm mb-4">Autres informations légales</h3>
              <div className="flex flex-wrap gap-4">
                {Object.entries(PAGE_META)
                  .filter(([key]) => key !== type)
                  .map(([key, page]) => (
                    <Link
                      key={key}
                      to={`/legal/${key}`}
                      className="text-[#C9A24D] hover:text-[#E6C77A] text-sm transition-colors"
                    >
                      {page.title}
                    </Link>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
